import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons/faPenToSquare';
import { faBolt } from '@fortawesome/pro-solid-svg-icons/faBolt';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import {
  Sitter,
  SitterService,
  TeachingSkillsService,
  SpecialExperiencesService,
  SpecialServicesService,
  SpecialSituationsService,
  LanguageService,
  SitterTeachingSkillsService,
  YearExperienceService,
  SitterCollegeService,
  CollegeService,
  CollegeAreaOfStudyService,
  CertificationsService,
  SitterStatsService,
  FavoriteSitterService,
  SitterRateSuggestionService,
} from 'us-web-services';
import { SitterNoteModal } from 'us-react-components/dist/SitterNoteModal/SitterNoteModal';
import { FlagProfileModal } from 'us-react-components/dist/FlagProfileModal/FlagProfileModal';
import { ConfirmModal } from 'us-react-components/dist/ConfirmModal/ConfirmModal';
import { ProfileVideoModal } from 'us-react-components/dist/ProfileVideoModal/ProfileVideoModal';
import { FamilyPaywallModal } from 'us-react-components/dist/FamilyPaywallModal/FamilyPaywallModal';

import { useDependencies } from '../../dependencies';
import { ConfigService } from '../../services/ConfigService/ConfigService';
import { ModalName } from '../../modals/constants';
import { useModal } from '../../modals/modalContext';
import { checkForPaywall } from '../../utils/PaywallUtil';
import { SitterProfileChrome } from '../SitterReviews/components/SitterProfileChrome';
import {
  SitterSnippet,
  SitterAbout,
  NotAcceptingFamiliesBanner,
  SitterOtherServices,
  SitterBadges,
  SitterAgeExperience,
  SitterRates,
  SitterTeachingSkills,
  SitterSpecialExperience,
  SitterSpecialServices,
  SitterSpecialSituations,
  SitterEducation,
  SitterCertifications,
  SitterLocation,
  AvailabilityStatusWrapper,
  SitterRelationship,
  SitterProfileSidePanel,
} from './components/SubComponents';
import {
  AvailabilityModal,
  TextAreaEditModal,
  MultiSelectEditModal,
  RatesEditModal,
  LocationEditModal,
  AgeExperienceEditModal,
  EducationEditModal,
  CertificationsEditModal,
} from './components/Modals';
import { Button, ButtonType } from '../../components/Button';

const config = new ConfigService();

const formatRate = (rate?: { minimumRate?: number; maximumRate?: number }): string => {
  if (!rate || (!rate.minimumRate && !rate.maximumRate)) return '';
  if (rate.minimumRate) {
    const min = `$${rate.minimumRate}`;
    const max =
      rate.maximumRate && rate.maximumRate !== rate.minimumRate
        ? ` - ${rate.maximumRate}`
        : '';
    return `${min}${max} per hour`;
  }
  return `$${rate.maximumRate} per hour`;
};

export const SitterProfile = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores, services: { tracker } } = useDependencies();
  const user = stores.user.user;

  type SitterLoadState = 'loading' | 'loaded' | 'not-found' | 'forbidden' | 'error';
  const [sitter, setSitter] = useState<Sitter | null>(null);
  const [loadState, setLoadState] = useState<SitterLoadState>('loading');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [stats, setStats] = useState<Parameters<typeof AvailabilityStatusWrapper>[0]['stats']>(undefined);
  const [education, setEducation] = useState<{
    id?: number;
    college?: { id?: number; name?: string };
    areaOfStudy?: { id?: number; name?: string };
    graduation?: string;
  } | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [hideConfirmOpen, setHideConfirmOpen] = useState(false);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [editAboutOpen, setEditAboutOpen] = useState(false);
  const [editTeachingSkillsOpen, setEditTeachingSkillsOpen] = useState(false);
  const [editSpecialExpOpen, setEditSpecialExpOpen] = useState(false);
  const [editSpecialServicesOpen, setEditSpecialServicesOpen] = useState(false);
  const [editSpecialSituationsOpen, setEditSpecialSituationsOpen] = useState(false);
  const [editLanguagesOpen, setEditLanguagesOpen] = useState(false);
  const [editOtherServicesOpen, setEditOtherServicesOpen] = useState(false);
  const [editRatesOpen, setEditRatesOpen] = useState(false);
  const [editLocationOpen, setEditLocationOpen] = useState(false);
  const [editAgeOpen, setEditAgeOpen] = useState(false);
  const [editEducationOpen, setEditEducationOpen] = useState(false);
  const [editCertificationsOpen, setEditCertificationsOpen] = useState(false);
  const [teachingSkillsOptions, setTeachingSkillsOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [specialExpOptions, setSpecialExpOptions] = useState<Array<{ id: number; name: string; description?: string }>>([]);
  const [specialServicesOptions, setSpecialServicesOptions] = useState<Array<{ id: number; name: string; description?: string }>>([]);
  const [specialSituationsOptions, setSpecialSituationsOptions] = useState<Array<{ id: number; name: string; description?: string }>>([]);
  const [languagesOptions, setLanguagesOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [ageExperiencesOptions, setAgeExperiencesOptions] = useState<Array<{ id: number; label: string }>>([]);
  const [collegeOptions, setCollegeOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [studyAreaOptions, setStudyAreaOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [certificationsOptions, setCertificationsOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [suggestedRate, setSuggestedRate] = useState<
    { rate?: number; defaultRate?: boolean } | undefined
  >(undefined);

  useEffect(() => {
    document.title = 'UrbanSitter - Caregiver Profile';
  }, []);

  useEffect(() => {
    const sitterId = Number(pathId);
    if (!sitterId || !user?.id) return;
    const controller = new AbortController();
    setLoadState('loading');
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterService.get(sitterId, {
          params: { userId: user.id, parentId: user.id },
          signal: controller.signal,
        });
        setSitter((resp.data?.data ?? null) as Sitter | null);
        setLoadState('loaded');
      } catch (err) {
        const e = err as { code?: string; response?: { status?: number } };
        if (e?.code === 'ERR_CANCELED') return;
        const status = e?.response?.status;
        if (status === 404) setLoadState('not-found');
        else if (status === 403) setLoadState('forbidden');
        else setLoadState('error');
      } finally {
        setLoaded(true);
      }
    };
    void load();
    return (): void => controller.abort();
  }, [pathId, user?.id, loadAttempt]);

  useEffect(() => {
    void (async (): Promise<void> => {
      try {
        const resp = await TeachingSkillsService.getByFilter();
        setTeachingSkillsOptions((resp.data?.data ?? []) as Array<{ id: number; name: string }>);
      } catch {
        setTeachingSkillsOptions([]);
      }
    })();
    void (async (): Promise<void> => {
      try {
        const resp = await SpecialExperiencesService.getByFilter();
        setSpecialExpOptions((resp.data?.data ?? []) as Array<{ id: number; name: string; description?: string }>);
      } catch {
        setSpecialExpOptions([]);
      }
    })();
    void (async (): Promise<void> => {
      try {
        const resp = await SpecialServicesService.getByFilter();
        setSpecialServicesOptions((resp.data?.data ?? []) as Array<{ id: number; name: string; description?: string }>);
      } catch {
        setSpecialServicesOptions([]);
      }
    })();
    void (async (): Promise<void> => {
      try {
        const resp = await SpecialSituationsService.getByFilter();
        setSpecialSituationsOptions((resp.data?.data ?? []) as Array<{ id: number; name: string; description?: string }>);
      } catch {
        setSpecialSituationsOptions([]);
      }
    })();
    void (async (): Promise<void> => {
      try {
        const resp = await LanguageService.getByFilter();
        setLanguagesOptions((resp.data?.data ?? []) as Array<{ id: number; name: string }>);
      } catch {
        setLanguagesOptions([]);
      }
    })();
    void (async (): Promise<void> => {
      try {
        const resp = await YearExperienceService.getByFilter();
        setAgeExperiencesOptions(
          (resp.data?.data ?? []).map((e) => ({ id: e.id, label: e.label })),
        );
      } catch {
        setAgeExperiencesOptions([]);
      }
    })();
    void (async (): Promise<void> => {
      try {
        const resp = await CollegeService.getByFilter();
        setCollegeOptions(
          ((resp.data?.data ?? []) as Array<{ id: number; name: string }>).filter(
            (c) => !!c.name,
          ),
        );
      } catch {
        setCollegeOptions([]);
      }
    })();
    void (async (): Promise<void> => {
      try {
        const resp = await CollegeAreaOfStudyService.getByFilter();
        setStudyAreaOptions(
          (resp.data?.data ?? []).map((s) => ({ id: s.id, name: s.name })),
        );
      } catch {
        setStudyAreaOptions([]);
      }
    })();
    void (async (): Promise<void> => {
      try {
        const resp = await CertificationsService.getByFilter();
        setCertificationsOptions(
          (resp.data?.data ?? []).map((c) => ({ id: c.id, name: c.name })),
        );
      } catch {
        setCertificationsOptions([]);
      }
    })();
  }, []);

  useEffect(() => {
    const sitterId = Number(pathId);
    if (!sitterId) return;
    void (async (): Promise<void> => {
      try {
        const resp = await SitterStatsService.get(sitterId);
        setStats(resp.data?.data);
      } catch {
        setStats(undefined);
      }
    })();
    void (async (): Promise<void> => {
      try {
        const resp = await SitterCollegeService.getByFilter({ params: { sitterId } });
        const list = (resp.data?.data ?? []) as Array<{
          id?: number;
          college?: { id?: number; name?: string };
          areaOfStudy?: { id?: number; name?: string };
          graduation?: string;
        }>;
        setEducation(list[0]);
      } catch {
        setEducation(undefined);
      }
    })();
  }, [pathId]);

  useEffect(() => {
    if (!sitter?.id || !sitter.address?.zip) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterRateSuggestionService.get({
          params: { kids: 1, sitterId: sitter.id, zip: sitter.address?.zip },
        });
        setSuggestedRate(resp.data?.data as typeof suggestedRate);
      } catch {
        setSuggestedRate(undefined);
      }
    };
    void load();

  }, [sitter?.id, sitter?.address?.zip]);

  const refreshSitter = async (): Promise<void> => {
    const sitterId = Number(pathId);
    if (!sitterId || !user?.id) return;
    const resp = await SitterService.get(sitterId, {
      params: { userId: user.id, parentId: user.id },
    });
    setSitter((resp.data?.data ?? null) as Sitter | null);
  };

  if (!loaded) {
    return (
      <div className='container py-8 text-center'>
        <i className='fa-solid fa-spinner fa-spin mr-2' />
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  if (sitter && sitter.active === false) {
    return (
      <div className='container py-8'>
        <h2 className='text-lg'>{t('pages.sitterProfile.notActive')}</h2>
      </div>
    );
  }

  if (loadState === 'not-found' || loadState === 'forbidden' || loadState === 'error') {
    return (
      <div className='container py-8'>
        <div className='us-card rounded p-5 max-w-2xl mx-auto'>
          <h2 className='text-xl font-semibold mb-2'>
            {t(loadState === 'error' ? 'common.errors.loadFailedTitle' : `common.errors.${loadState === 'not-found' ? 'notFound' : 'forbidden'}`)}
          </h2>
          <p className='mb-4'>
            {t(loadState === 'error' ? 'common.errors.loadFailedBody' : `common.errors.${loadState === 'not-found' ? 'notFoundBody' : 'forbiddenBody'}`)}
          </p>
          {loadState === 'error' && (
            <Button
              buttonType={ButtonType.primary}
              onClick={(): void => setLoadAttempt((n) => n + 1)}
            >
              {t('common.errors.retry')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!sitter?.id) return <div className='container py-8' />;

  const isSelf = !!user?.id && user.id === sitter.id;
  const isParent = !!user?.parent;
  const isSitter = !!user?.sitter;
  // Caregiver-app viewers are other sitters, not parent members, so the
  // favorite heart (which is gated on `isMember` in Angular's sitter-snippet)
  // should not render here.
  const isMember = false;
  const canEdit = isSelf;
  const recommendLink =
    typeof window !== 'undefined'
      ? `${window.location.origin}/app/sitter/${sitter.id}/recommend`
      : `/app/sitter/${sitter.id}/recommend`;

  const rate = (sitter.rates ?? []).find(
    (r) => (r.minimumRate ?? 0) > 0 || (r.maximumRate ?? 0) > 0,
  );
  const rateText = formatRate(rate);
  const rateLabel = rateText ? ' for 1 child' : '';

  // Mirrors DisplayService.getDistanceText — prefer the URL param
  // (`sitterDistance`) that sitter cards append when linking to the profile,
  // otherwise fall back to the relationship payload embedded on the sitter.
  const distanceParam = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('sitterDistance')
    : null;
  const distanceValue = ((): number | null => {
    const raw = distanceParam
      ? Number(distanceParam)
      : (sitter.relationship as { distance?: number } | undefined)?.distance;
    if (raw == null || Number.isNaN(raw)) return null;
    return raw;
  })();
  const distanceText =
    distanceValue == null
      ? ''
      : distanceValue <= 1
        ? '< 1 mile'
        : `${distanceValue} miles`;

  const trackProfileAction = (action: string): void => {
    tracker.track('clicked-button', { action, component: 'Sitter Profile' });
  };
  const modal = useModal();
  const openIdentity = (): void => modal?.setModalName(ModalName.IdentityVerification);
  const onBook = (): void => {
    trackProfileAction('Book CTA');
    stores.verification.checkVerification(() => {
      navigate(`/parent/bookings/select?sitterId=${sitter.id}`);
    }, openIdentity);
  };
  const onMessage = (): void => {
    trackProfileAction('Message CTA');
    stores.verification.checkVerification(() => {
      checkForPaywall({
        userId: user?.id,
        sitter,
        action: (): void => {
          navigate(`/parent/${user?.id}/messages/${sitter.id}`);
        },
        openPaywall: (): void => setPaywallOpen(true),
      });
    }, openIdentity);
  };
  const onInterview = (): void => {
    trackProfileAction('Interview CTA');
    stores.verification.checkVerification(() => {
      navigate(`/parent/interviews/select?sitterId=${sitter.id}`);
    }, openIdentity);
  };

  const toggleHidden = async (): Promise<void> => {
    try {
      await SitterService.patch(sitter.id as number, {
        ...sitter,
        relationship: {
          ...(sitter.relationship ?? {}),
          blocked: !sitter.relationship?.blocked,
        },
      } as unknown as Parameters<typeof SitterService.patch>[1]);
      toast.success(
        sitter.relationship?.blocked ? 'Caregiver shown' : 'Caregiver hidden',
      );
      await refreshSitter();
    } catch {
      toast.error("Couldn't update visibility.");
    }
  };

  const saveAbout = async (value: string): Promise<void> => {
    await SitterService.patch(sitter.id as number, {
      ...sitter,
      about: value,
    } as unknown as Parameters<typeof SitterService.patch>[1]);
    toast.success('Bio saved.');
    await refreshSitter();
  };

  const saveTeachingSkills = async (ids: number[]): Promise<void> => {
    if (!sitter.id) return;
    const current = (sitter.teachingSkills ?? []).map((s) => s.id);
    const toAdd = ids.filter((id) => !current.includes(id));
    const toRemove = current.filter((id) => !ids.includes(id));
    try {
      for (const id of toAdd) {
        await SitterTeachingSkillsService.create({
          sitterId: sitter.id,
          teachingSkillId: id,
        } as never);
      }
      for (const id of toRemove) {
        if (SitterTeachingSkillsService.delete) {
          await SitterTeachingSkillsService.delete(id);
        }
      }
      toast.success('Teaching skills updated.');
      await refreshSitter();
    } catch {
      toast.error("Couldn't update. Try again.");
    }
  };

  const savePatchField = async (
    field: 'specialExperiences' | 'specialServices' | 'specialSituations' | 'languages',
    options: Array<{ id: number; name: string }>,
    ids: number[],
    otherName?: string,
  ): Promise<void> => {
    if (!sitter.id) return;
    const newItems: Array<{ id?: number; name?: string }> = options
      .filter((o) => ids.includes(o.id))
      .map((o) => ({ id: o.id, name: o.name }));
    if (field === 'languages' && otherName) {
      const trimmed = otherName.trim();
      if (trimmed && !options.some((o) => o.name.toLowerCase() === trimmed.toLowerCase())) {
        newItems.push({ name: trimmed });
      }
    }
    try {
      await SitterService.patch(sitter.id, {
        ...sitter,
        [field]: newItems,
      } as unknown as Parameters<typeof SitterService.patch>[1]);
      toast.success('Saved.');
      await refreshSitter();
    } catch {
      toast.error("Couldn't save. Try again.");
    }
  };

  const presetLanguageNames = new Set(languagesOptions.map((l) => l.name.toLowerCase()));
  const initialOtherLanguage = (sitter.languages ?? [])
    .filter((l) => !!l)
    .filter((l) => !l.id || (l.name && !presetLanguageNames.has(l.name.toLowerCase())))
    .map((l) => l.name ?? '')
    .filter(Boolean)
    .join(', ');

  const saveOtherServices = async (ids: number[]): Promise<void> => {
    if (!sitter.id) return;
    try {
      await SitterService.patch(sitter.id, {
        ...sitter,
        willSitDog: ids.includes(1),
        willSitCat: ids.includes(2),
        willSitOtherPet: ids.includes(3),
        willSitHome: ids.includes(4),
        willSitSenior: ids.includes(5),
      } as unknown as Parameters<typeof SitterService.patch>[1]);
      toast.success('Other services updated.');
      await refreshSitter();
    } catch {
      toast.error("Couldn't save. Try again.");
    }
  };

  const saveRates = async (patch: {
    rates: Array<{ minimumRate: number; maximumRate: number }>;
    maxKids?: number;
    minimumHours?: number;
    acceptsFixedRates?: boolean;
    willSitSingleFamily?: boolean;
  }): Promise<void> => {
    if (!sitter.id) return;
    await SitterService.patch(sitter.id, {
      ...sitter,
      ...patch,
    } as unknown as Parameters<typeof SitterService.patch>[1]);
    toast.success('Rates updated.');
    await refreshSitter();
  };

  const saveLocation = async (patch: {
    address: {
      street?: string;
      street2?: string;
      city: string;
      state: string;
      zip: string;
      neighborhood?: string;
    };
    worksWithin?: number;
  }): Promise<void> => {
    if (!sitter.id) return;
    await SitterService.patch(sitter.id, {
      ...sitter,
      ...patch,
    } as unknown as Parameters<typeof SitterService.patch>[1]);
    toast.success('Location updated.');
    await refreshSitter();
  };

  const saveAgeExperiences = async (experiences: {
    infant: { id: number };
    toddler: { id: number };
    school: { id: number };
  }): Promise<void> => {
    if (!sitter.id) return;
    const lookup = (id: number): { id: number; label?: string } | undefined => {
      const found = ageExperiencesOptions.find((a) => a.id === id);
      return found ? { id: found.id, label: found.label } : undefined;
    };
    await SitterService.patch(sitter.id, {
      ...sitter,
      experiences: {
        infant: lookup(experiences.infant.id),
        toddler: lookup(experiences.toddler.id),
        school: lookup(experiences.school.id),
      },
    } as unknown as Parameters<typeof SitterService.patch>[1]);
    toast.success('Experience updated.');
    await refreshSitter();
  };

  const saveEducation = async (data: {
    id?: number;
    collegeId: number;
    areaOfStudyId?: number;
    graduationMonth?: string;
    graduationYear?: string;
  }): Promise<void> => {
    if (!sitter.id) return;
    const college = collegeOptions.find((c) => c.id === data.collegeId);
    const areaOfStudy = data.areaOfStudyId
      ? studyAreaOptions.find((s) => s.id === data.areaOfStudyId)
      : undefined;
    const graduation =
      data.graduationMonth && data.graduationYear
        ? new Date(`${data.graduationMonth} 1, ${data.graduationYear}`).toISOString()
        : undefined;
    const payload = {
      id: data.id,
      sitterId: sitter.id,
      college,
      areaOfStudy,
      graduation,
    } as unknown as Parameters<typeof SitterCollegeService.patch>[1];
    if (data.id) {
      const resp = await SitterCollegeService.patch(data.id, payload);
      setEducation(resp.data?.data as typeof education);
    } else {
      const resp = await SitterCollegeService.create(payload as never);
      setEducation(resp.data?.data as typeof education);
    }
    toast.success('Education updated.');
  };

  const deleteEducation = async (id: number): Promise<void> => {
    await SitterCollegeService.delete(id);
    setEducation(undefined);
    toast.success('Education cleared.');
  };

  const saveCertifications = async (
    selected: Array<{ id: number; name: string }>,
  ): Promise<void> => {
    if (!sitter.id) return;
    await SitterService.patch(sitter.id, {
      ...sitter,
      certifications: selected,
    } as unknown as Parameters<typeof SitterService.patch>[1]);
    toast.success('Certifications updated.');
    await refreshSitter();
  };

  const toggleFavorite = async (): Promise<void> => {
    if (!sitter.id) return;
    try {
      if (sitter.relationship?.bookmarked) {
        const favId = (sitter.relationship as { bookmarkedId?: number }).bookmarkedId;
        if (favId) await FavoriteSitterService.delete(favId);
      } else {
        await FavoriteSitterService.create({
          sitterId: sitter.id,
          parentId: user?.id,
        } as never);
      }
      await refreshSitter();
    } catch {
      toast.error("Couldn't update favorite. Try again.");
    }
  };

  return (
    <div className='container py-6 max-w-6xl mx-auto'>
      <SitterProfileChrome
        sitter={sitter as unknown as Parameters<typeof SitterProfileChrome>[0]['sitter']}
        activeTab='summary'
        viewerUserId={user?.id}
      />

      <NotAcceptingFamiliesBanner sitter={sitter} />

      <div className='grid grid-cols-1 md:grid-cols-12 gap-6'>
        <div className='md:col-span-8'>
          <SitterSnippet
            sitter={sitter}
            isSelf={isSelf}
            isMember={isMember}
            isSitter={isSitter}
            rateText={rateText}
            rateLabel={rateLabel}
            distanceText={distanceText}
            onPlayVideo={(): void => setVideoOpen(true)}
            onFavoriteToggle={toggleFavorite}
          />
          {sitter.userStats?.lastAccessLabel && (
            <div className='mb-3 text-sm text-neutral-600'>
              <FontAwesomeIcon icon={faBolt} className='text-sapphire mr-1' />
              <span>Active {sitter.userStats.lastAccessLabel.toLowerCase()}</span>
            </div>
          )}
          <AvailabilityStatusWrapper
            sitter={sitter}
            canEdit={canEdit}
            stats={stats}
            onOpenAvailabilityModal={(): void => setAvailabilityOpen(true)}
          />
          <SitterRelationship
            sitter={sitter}
            userId={user?.id}
            onOpenNoteModal={(): void => setNoteOpen(true)}
          />
        </div>

        <div id='profile-side-panel' className='md:col-span-4'>
          <SitterProfileSidePanel
            sitter={sitter}
            user={user}
            isParent={isParent}
            isSelf={isSelf}
            canEdit={canEdit}
            page='summary'
            onBook={onBook}
            onMessage={onMessage}
            onInterview={onInterview}
            onToggleHidden={(): void => setHideConfirmOpen(true)}
            onOpenFlagModal={(): void => setFlagOpen(true)}
            onReviewClick={(): void => navigate(`/sitter/${sitter.id}/reviews`)}
            recommendLink={recommendLink}
          />
        </div>

        <div id='profile-details' className='md:col-span-8 space-y-8'>
          {/* sitter-about-wrapper:
              col-md-8 = About+edit + Other Services+edit (stacked); col-md-4 = Badges. */}
          <div className='bg-neutral-50 border border-neutral-200 rounded-lg p-6'>
            <div className='grid grid-cols-1 md:grid-cols-12 gap-6'>
              <div className='md:col-span-8 space-y-4 [&>div]:bg-white [&>div]:border [&>div]:border-neutral-200 [&>div]:rounded [&>div]:p-4'>
                <div>
                  {canEdit && (
                    <div className='text-right mb-2'>
                      <Button
                        buttonType={ButtonType.tertiary}
                        onClick={(): void => setEditAboutOpen(true)}
                        className='cursor-pointer text-sapphire text-sm bg-transparent border-0 p-0'
                        aria-label='Edit'
                        title='Edit'
                      >
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </Button>
                    </div>
                  )}
                  <SitterAbout
                    sitter={sitter}
                    canEdit={canEdit}
                    onOpenEditAbout={(): void => setEditAboutOpen(true)}
                    onOpenEditLanguages={(): void => setEditLanguagesOpen(true)}
                  />
                </div>
                <div>
                  {canEdit && (
                    <div className='text-right mb-2'>
                      <Button
                        buttonType={ButtonType.tertiary}
                        onClick={(): void => setEditOtherServicesOpen(true)}
                        className='cursor-pointer text-sapphire text-sm bg-transparent border-0 p-0'
                        aria-label='Edit'
                        title='Edit'
                      >
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </Button>
                    </div>
                  )}
                  <SitterOtherServices sitter={sitter} canEdit={canEdit} />
                </div>
              </div>
              <div className='md:col-span-4 bg-white border border-neutral-200 rounded p-4'>
                <SitterBadges sitter={sitter} />
              </div>
            </div>
          </div>

          {/* sitter-experience-wrapper: 3 cols x 2 rows */}
          <div className='bg-neutral-50 border border-neutral-200 rounded-lg p-6'>
            <h3 className='text-lg font-semibold mb-4 pb-2 border-b border-neutral-200'>Experience</h3>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4 [&>div]:bg-white [&>div]:border [&>div]:border-neutral-200 [&>div]:rounded [&>div]:p-4'>
              <div>
                {canEdit && (
                  <div className='text-right mb-1'>
                    <Button
                      buttonType={ButtonType.tertiary}
                      id='edit-age-experience-button'
                      onClick={(): void => setEditAgeOpen(true)}
                      className='cursor-pointer text-sapphire text-sm bg-transparent border-0 p-0'
                      aria-label='Edit'
                      title='Edit'
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                  </div>
                )}
                <SitterAgeExperience sitter={sitter} />
              </div>
              <div>
                {canEdit && (
                  <div className='text-right mb-1'>
                    <Button
                      buttonType={ButtonType.tertiary}
                      onClick={(): void => setEditRatesOpen(true)}
                      className='cursor-pointer text-sapphire text-sm bg-transparent border-0 p-0'
                      aria-label='Edit'
                      title='Edit'
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                  </div>
                )}
                <SitterRates sitter={sitter} />
              </div>
              <div>
                {canEdit && (
                  <div className='text-right mb-1'>
                    <Button
                      buttonType={ButtonType.tertiary}
                      onClick={(): void => setEditTeachingSkillsOpen(true)}
                      className='cursor-pointer text-sapphire text-sm bg-transparent border-0 p-0'
                      aria-label='Edit'
                      title='Edit'
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                  </div>
                )}
                <SitterTeachingSkills sitter={sitter} />
              </div>
              <div>
                {canEdit && (
                  <div className='text-right mb-1'>
                    <Button
                      buttonType={ButtonType.tertiary}
                      onClick={(): void => setEditSpecialExpOpen(true)}
                      className='cursor-pointer text-sapphire text-sm bg-transparent border-0 p-0'
                      aria-label='Edit'
                      title='Edit'
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                  </div>
                )}
                <SitterSpecialExperience sitter={sitter} />
              </div>
              <div>
                {canEdit && (
                  <div className='text-right mb-1'>
                    <Button
                      buttonType={ButtonType.tertiary}
                      onClick={(): void => setEditSpecialServicesOpen(true)}
                      className='cursor-pointer text-sapphire text-sm bg-transparent border-0 p-0'
                      aria-label='Edit'
                      title='Edit'
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                  </div>
                )}
                <SitterSpecialServices sitter={sitter} />
              </div>
              <div>
                {canEdit && (
                  <div className='text-right mb-1'>
                    <Button
                      buttonType={ButtonType.tertiary}
                      onClick={(): void => setEditSpecialSituationsOpen(true)}
                      className='cursor-pointer text-sapphire text-sm bg-transparent border-0 p-0'
                      aria-label='Edit'
                      title='Edit'
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                  </div>
                )}
                <SitterSpecialSituations sitter={sitter} />
              </div>
            </div>
          </div>

          {/* sitter-background-wrapper: Education + Certifications in 2-col */}
          <div className='bg-neutral-50 border border-neutral-200 rounded-lg p-6'>
            <h3 className='text-lg font-semibold mb-4 pb-2 border-b border-neutral-200'>Background</h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 [&>div]:bg-white [&>div]:border [&>div]:border-neutral-200 [&>div]:rounded [&>div]:p-4'>
              <div>
                {canEdit && (
                  <div className='text-right mb-1'>
                    <Button
                      buttonType={ButtonType.tertiary}
                      id='edit-education-button'
                      onClick={(): void => setEditEducationOpen(true)}
                      className='cursor-pointer text-sapphire text-sm bg-transparent border-0 p-0'
                      aria-label='Edit'
                      title='Edit'
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                  </div>
                )}
                <SitterEducation sitter={sitter} />
              </div>
              <div>
                {canEdit && (
                  <div className='text-right mb-1'>
                    <Button
                      buttonType={ButtonType.tertiary}
                      id='edit-certifications-button'
                      onClick={(): void => setEditCertificationsOpen(true)}
                      className='cursor-pointer text-sapphire text-sm bg-transparent border-0 p-0'
                      aria-label='Edit'
                      title='Edit'
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                  </div>
                )}
                <SitterCertifications sitter={sitter} />
              </div>
            </div>
          </div>

          {/* sitter-location-wrapper */}
          <div className='bg-neutral-50 border border-neutral-200 rounded-lg p-6'>
            <h3 className='text-lg font-semibold mb-4 pb-2 border-b border-neutral-200'>Location</h3>
            <SitterLocation
              sitter={sitter}
              isSelf={isSelf}
              onEditLocation={(): void => setEditLocationOpen(true)}
            />
          </div>
        </div>
      </div>

      <ProfileVideoModal
        isOpen={videoOpen}
        onClose={(): void => setVideoOpen(false)}
        vimeoId={sitter.vimeoId}
      />
      {sitter.id && user?.id && (
        <SitterNoteModal
          isOpen={noteOpen}
          onClose={(): void => setNoteOpen(false)}
          sitterId={sitter.id}
          parentId={user.id}
          initialNote={
            sitter.relationship?.parentNote as
              | { id?: number; comment?: string }
              | undefined
          }
          onSaved={(): void => {
            refreshSitter();
          }}
        />
      )}
      {sitter.id && user?.id && (
        <FlagProfileModal
          isOpen={flagOpen}
          onClose={(): void => setFlagOpen(false)}
          flaggedId={sitter.id}
          flaggingId={user.id}
        />
      )}
      <ConfirmModal
        isOpen={hideConfirmOpen}
        onClose={(): void => setHideConfirmOpen(false)}
        title={
          sitter.relationship?.blocked
            ? 'Show this caregiver?'
            : 'Hide this caregiver?'
        }
        body={
          sitter.relationship?.blocked
            ? 'You will see this caregiver in search results again.'
            : 'This caregiver will no longer appear in your search results.'
        }
        confirmLabel={sitter.relationship?.blocked ? 'Show' : 'Hide'}
        onConfirm={toggleHidden}
      />
      <AvailabilityModal
        isOpen={availabilityOpen}
        onClose={(): void => setAvailabilityOpen(false)}
        sitter={sitter}
        canEdit={canEdit}
        onSaved={refreshSitter}
        lastCalendarUpdateLabel={
          (sitter as unknown as { stats?: { lastCalendarUpdate?: { label?: string } } })
            .stats?.lastCalendarUpdate?.label
        }
      />
      <TextAreaEditModal
        isOpen={editAboutOpen}
        onClose={(): void => setEditAboutOpen(false)}
        title='Edit Basics and Bio'
        label={`About ${sitter.firstName ?? ''}`}
        helperText='Text outside this box may be behind a "more" link on your profile.'
        initialValue={sitter.about ?? ''}
        maxLength={2000}
        onSave={saveAbout}
      />

      <MultiSelectEditModal
        isOpen={editTeachingSkillsOpen}
        onClose={(): void => setEditTeachingSkillsOpen(false)}
        title={t('pages.sitterProfile.modals.editTeachingSkills')}
        options={teachingSkillsOptions.map((o) => ({ id: o.id, name: o.name }))}
        initialSelected={(sitter.teachingSkills ?? []).map((s) => s.id)}
        onSave={saveTeachingSkills}
      />

      <MultiSelectEditModal
        isOpen={editSpecialExpOpen}
        onClose={(): void => setEditSpecialExpOpen(false)}
        title={t('pages.sitterProfile.modals.editSpecialExperience')}
        options={specialExpOptions}
        initialSelected={(sitter.specialExperiences ?? []).flatMap((s) => (s.id != null ? [s.id] : []))}
        onSave={(ids): Promise<void> => savePatchField('specialExperiences', specialExpOptions, ids)}
      />

      <MultiSelectEditModal
        isOpen={editSpecialServicesOpen}
        onClose={(): void => setEditSpecialServicesOpen(false)}
        title={t('pages.sitterProfile.modals.editSpecialServices')}
        options={specialServicesOptions}
        initialSelected={(sitter.specialServices ?? []).flatMap((s) => (s.id != null ? [s.id] : []))}
        onSave={(ids): Promise<void> => savePatchField('specialServices', specialServicesOptions, ids)}
      />

      <MultiSelectEditModal
        isOpen={editSpecialSituationsOpen}
        onClose={(): void => setEditSpecialSituationsOpen(false)}
        title={t('pages.sitterProfile.modals.editSpecialSituations')}
        options={specialSituationsOptions}
        initialSelected={(sitter.specialSituations ?? []).flatMap((s) => (s.id != null ? [s.id] : []))}
        onSave={(ids): Promise<void> => savePatchField('specialSituations', specialSituationsOptions, ids)}
      />

      <MultiSelectEditModal
        isOpen={editLanguagesOpen}
        onClose={(): void => setEditLanguagesOpen(false)}
        title={t('pages.sitterProfile.modals.editLanguages')}
        options={languagesOptions}
        initialSelected={(sitter.languages ?? [])
          .filter((s) => s.id && presetLanguageNames.has((s.name ?? '').toLowerCase()))
          .map((s) => s.id as number)}
        onSave={(ids, otherName): Promise<void> =>
          savePatchField('languages', languagesOptions, ids, otherName)
        }
        allowOther
        otherLabel='Other:'
        otherPlaceholder='Other Language'
        initialOther={initialOtherLanguage}
      />

      <MultiSelectEditModal
        isOpen={editOtherServicesOpen}
        onClose={(): void => setEditOtherServicesOpen(false)}
        title={t('pages.sitterProfile.modals.editOtherServices')}
        options={[
          { id: 1, name: 'Dogs' },
          { id: 2, name: 'Cats' },
          { id: 3, name: 'Other pets' },
          { id: 4, name: 'Household services' },
          { id: 5, name: 'Senior care' },
        ]}
        initialSelected={[
          ...(sitter.willSitDog ? [1] : []),
          ...(sitter.willSitCat ? [2] : []),
          ...(sitter.willSitOtherPet ? [3] : []),
          ...(sitter.willSitHome ? [4] : []),
          ...(sitter.willSitSenior ? [5] : []),
        ]}
        onSave={saveOtherServices}
      />

      <RatesEditModal
        isOpen={editRatesOpen}
        onClose={(): void => setEditRatesOpen(false)}
        initialRates={sitter.rates ?? []}
        initialMaxKids={sitter.maxKids}
        initialMinimumHours={sitter.minimumHours}
        initialAcceptsFixedRates={sitter.acceptsFixedRates}
        initialWillSitSingleFamily={sitter.willSitSingleFamily}
        suggestedRate={suggestedRate}
        onSave={saveRates}
      />

      <LocationEditModal
        isOpen={editLocationOpen}
        onClose={(): void => setEditLocationOpen(false)}
        initialAddress={sitter.address as Partial<{ street: string; street2: string; city: string; state: string; zip: string; neighborhood: string; }>}
        initialWorksWithin={sitter.worksWithin}
        onSave={saveLocation}
      />

      <AgeExperienceEditModal
        isOpen={editAgeOpen}
        onClose={(): void => setEditAgeOpen(false)}
        ageExperiences={ageExperiencesOptions}
        initialExperiences={sitter.experiences}
        onSave={saveAgeExperiences}
      />

      <EducationEditModal
        isOpen={editEducationOpen}
        onClose={(): void => setEditEducationOpen(false)}
        collegeOptions={collegeOptions}
        studyAreaOptions={studyAreaOptions}
        initialEducation={education}
        onSave={saveEducation}
        onDelete={deleteEducation}
      />

      <CertificationsEditModal
        isOpen={editCertificationsOpen}
        onClose={(): void => setEditCertificationsOpen(false)}
        certifications={certificationsOptions}
        initialSelected={(sitter.certifications ?? []).map((c) => ({
          id: c.id,
          name: c.name ?? '',
        }))}
        onSave={saveCertifications}
      />

      {user?.id && (
        <FamilyPaywallModal
          showModal={paywallOpen}
          onClose={(): void => setPaywallOpen(false)}
          onSuccess={(): void => {
            setPaywallOpen(false);
            navigate(`/parent/${user.id}/messages/${sitter.id}`);
          }}
          sitter={sitter as Sitter}
          userId={user.id}
          stripeKey={config.STRIPE_KEY}
          recaptchaKey={config.RECAPTCHA_KEY}
        />
      )}
    </div>
  );
});
